import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context/index.js'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions/index.js'
import { getMainDefinition } from '@apollo/client/utilities/index.js'
import { createUploadLink } from 'apollo-upload-client'
import { createClient } from 'graphql-ws'
import { differenceBy, fromPairs, sortBy, toPairs } from 'lodash'
import config from '~/common/config/config'
import { timingLink } from '~/common/telemetry/timing-link'
import { getEventScopeCacheKey } from '~/common/utils/eventScopeCacheKey'
import { forceLogout } from '~/common/utils/forceLogout'
import { getUserBearerToken, getUserToken } from './lib/firebase'

const GRAPHQL_API_URL = config.graphqlApiUrl
const SUBSCRIPTIONS_URL = config.subscriptionsUrl

function getLink() {
  const uploadLink = createUploadLink({
    uri: GRAPHQL_API_URL,
    headers: { 'Apollo-Require-Preflight': 'true' },
    credentials: 'include', // CRITICAL: Send sealed session cookie with every request
  })

  if (typeof window === 'undefined') {
    return uploadLink as unknown as ApolloLink
  }

  const wsLink = new GraphQLWsLink(
    createClient({
      url: SUBSCRIPTIONS_URL,
      connectionParams: async () => {
        // Try to get Firebase token for legacy auth fallback
        const firebaseToken = await getUserToken()
        // If there's a Firebase token, send it. Otherwise, sealed session cookie
        // will be sent automatically with the WebSocket upgrade request
        return firebaseToken ? { authToken: firebaseToken } : {}
      },
      lazy: true,
    }),
  )

  return split(
    // https://www.apollographql.com/docs/react/data/subscriptions/
    // Split subscriptions and normal operations to the appropriate links
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    uploadLink as unknown as ApolloLink,
  )
}

const link = getLink()

const AUTH_PROVIDER_SYNC_CHANNEL = 'doss_auth_provider_sync'

/**
 * Apollo Link that checks the server's authProvider in response extensions.
 * If there's a mismatch with the client's configured provider, forces logout.
 * This catches auth provider changes without polling - detection happens on
 * any GraphQL request. Broadcasts to other tabs so they also log out.
 */
const authProviderCheckLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const serverProvider = response.extensions?.authProvider

    // Only check if we got a valid provider value from the server
    if (serverProvider === 'firebase' || serverProvider === 'workos') {
      if (serverProvider !== config.authProvider) {
        // Notify other tabs before logging out
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel(AUTH_PROVIDER_SYNC_CHANNEL)
          channel.postMessage({ type: 'auth_provider_changed' })
          channel.close()
        }

        // Auth provider changed - force logout on next tick to let current response complete
        setTimeout(() => {
          void forceLogout()
        }, 0)
      }
    }

    return response
  })
})

// Listen for auth provider changes from other tabs
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  const authProviderChannel = new BroadcastChannel(AUTH_PROVIDER_SYNC_CHANNEL)
  authProviderChannel.onmessage = (event) => {
    if (event.data?.type === 'auth_provider_changed') {
      // Skip if on auth pages
      if (!window.location.pathname.startsWith('/auth/')) {
        void forceLogout()
      }
    }
  }
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const authLink = setContext(async (_, { headers }: any) => {
  // Only set Authorization header for Firebase auth (legacy fallback)
  // For WorkOS auth, sealed session cookie is sent automatically via credentials: 'include'
  const bearerToken = await getUserBearerToken()

  return {
    headers: {
      ...headers,
      // Only add authorization header if we have a Firebase token
      ...(bearerToken ? { authorization: bearerToken } : {}),
    },
  }
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([timingLink, authProviderCheckLink, authLink, link]),
  cache: new InMemoryCache({
    // Any object that doesn't have a default ID requires a merge function to determine how to
    // handle incoming objects in the cache
    typePolicies: {
      // Subscription payloads; avoid normalizing distinct progress events into one object
      FileProgress: {
        keyFields: false,
      },
      LineChartAggregation: {
        // This tells Apollo *not* to normalize by (typename, id)
        // and instead store each result object as-is.
        keyFields: false,
      },
      PieChartAggregation: {
        // This tells Apollo *not* to normalize by (typename, id)
        // and instead store each result object as-is.
        keyFields: false,
      },
      PivotDimension: {
        // This tells Apollo *not* to normalize by (typename, id)
        // and instead store each result object as-is.
        keyFields: false,
      },
      Query: {
        fields: {
          paginatedNotifications: {
            // Don't cache separate results based on
            // any of this field's arguments.
            keyArgs: false,
            merge: (existing, incoming) => {
              if (!existing) {
                return incoming
              }
              if (!incoming) {
                return existing
              }

              // Get the notifications that are in the existing but not in the incoming, then add the
              // notifications from the incoming in
              const existingNotifications = existing.notifications
              const incomingNotifications = incoming.notifications
              const unchangedNotifications = differenceBy(
                // notifications can have isUnread and isArchived fields changed
                // make sure to update the existing ones in case they are changed
                existingNotifications,
                incomingNotifications,
                (notification: { __ref: string }) => notification.__ref,
              )

              // order is consistent here, results come in reverse chrono order from the backend
              const allNotifications = [...unchangedNotifications, ...incomingNotifications]

              // Merge the notifications together
              const mergedRefs = {
                offset: incoming.offset,
                notifications: allNotifications,
                hasNext: incoming.hasNext,
                totalUnreadCount: incoming.totalUnreadCount,
                totalCount: incoming.totalCount,
                __typename: incoming.__typename,
              }
              return mergedRefs
            },
          },
          paginatedTableRows: {
            keyArgs: (args) => {
              if (!args || !args.input) {
                return undefined
              }
              // We need the properties to be sorted so that the cache key is deterministic
              return JSON.stringify(fromPairs(sortBy(toPairs(args.input))))
            },
          },
          paginatedEvents: {
            keyArgs: (args) => {
              if (!args?.input) {
                return undefined
              }
              return getEventScopeCacheKey(args.input)
            },
            merge(existing, incoming) {
              if (!existing) {
                return incoming
              }
              if (!incoming) {
                return existing
              }

              // Union deduped by ref: incoming may be a fetchMore page or a single pushed event.
              const existingEvents = existing.events
              const incomingEvents = incoming.events
              const unchangedEvents = differenceBy(
                existingEvents,
                incomingEvents,
                (event: { event: { __ref: string } }) => event.event.__ref,
              )
              const allEvents = [...unchangedEvents, ...incomingEvents]

              return {
                cursor: incoming.cursor,
                events: allEvents,
                hasNext: incoming.hasNext,
                __typename: incoming.__typename,
              }
            },
          },
          paginatedComments: {
            keyArgs: (args) => {
              if (!args?.input) {
                return undefined
              }
              return [args.input.objectId, args.input.objectType].join('-')
            },
            merge(existing, incoming) {
              if (!existing) {
                return incoming
              }
              if (!incoming) {
                return existing
              }

              // Union deduped by ref: incoming may be a fetchMore page or a single pushed comment.
              const existingComments = existing.comments
              const incomingComments = incoming.comments
              const unchangedComments = differenceBy(
                existingComments,
                incomingComments,
                (comment: { comment: { __ref: string } }) => comment.comment.__ref,
              )
              const allComments = [...unchangedComments, ...incomingComments]

              return {
                cursor: incoming.cursor,
                comments: allComments,
                hasNext: incoming.hasNext,
                __typename: incoming.__typename,
              }
            },
          },
        },
      },
    },
  }),
})
